import { Component, OnInit } from '@angular/core';
import Swal from 'sweetalert2';
import {
  BonosService,
  Campana,
  ConfigBonos,
  GastoPorMoneda,
  KpiCampana,
} from 'src/app/shared/services/bonos.service';

/**
 * El panel de bonos, para operaciones.
 *
 * Tres vistas sobre lo mismo:
 *   1. Las campañas, con su cupo y su bolsa, y los botones que las mueven.
 *   2. El gasto por moneda, que es lo que Finanzas necesita para provisionar.
 *   3. Los KPI por campaña, que es lo que Marketing prometió medir.
 *
 * Lo que hay que mirar acá no es «cuánto se pagó» sino **comprometido**: son
 * reservas vivas que se van a pagar si esos clientes cumplen su condición. Sin
 * ese número el gasto engaña — se ve bajo justo antes de que una tanda de
 * bonos retenidos se acredite junta.
 *
 * Este panel no decide ninguna regla de plata. Qué moneda corresponde a cada
 * alcance, cuándo hace falta un segundo operador y cuánto se puede gastar en
 * el mes lo contesta el backend; acá se pide y se muestra. Una regla de plata
 * duplicada en el front es la garantía de que en seis meses las dos versiones
 * no van a decir lo mismo.
 */
@Component({
  selector: 'app-bonos-admin',
  templateUrl: './bonos-admin.component.html',
  styleUrls: ['./bonos-admin.component.sass'],
})
export class BonosAdminComponent implements OnInit {
  cargando = true;
  error = '';

  campanas: Campana[] = [];
  config: ConfigBonos | null = null;

  vista: 'campanas' | 'gasto' | 'kpi' = 'campanas';

  gasto: GastoPorMoneda[] = [];
  kpis: KpiCampana[] = [];
  cargandoReporte = false;

  /** Filtro de la lista. Vacío = todas. */
  filtroEstado = '';

  /** El formulario de creación, como modal. */
  mostrarNueva = false;

  // --- configuración del módulo ---
  mostrarConfig = false;
  guardandoConfig = false;
  /** Copia editable. Se trabaja sobre una copia para poder cancelar sin efecto. */
  editable: ConfigBonos | null = null;
  original: ConfigBonos | null = null;
  /** Las monedas con techo, en orden estable para que los campos no salten. */
  monedasConTecho: string[] = [];
  /** Las monedas con umbral de doble control, en orden estable. */
  monedasConUmbral: string[] = [];

  /** La campaña abierta en el tablero. `null` = la lista. */
  campanaAbierta: Campana | null = null;

  /** Id de la campaña sobre la que hay una acción en curso. */
  moviendo: number | null = null;

  constructor(private bonos: BonosService) {}

  async ngOnInit(): Promise<void> {
    await this.cargar();
  }

  private async cargar(): Promise<void> {
    this.cargando = true;
    this.error = '';
    try {
      // La config va en paralelo pero no puede tumbar la pantalla: si falla,
      // se pierden los avisos de arriba, no la lista de campañas.
      const [campanas, config] = await Promise.all([
        this.bonos.listarCampanas(),
        this.bonos.config().catch(() => null),
      ]);
      this.campanas = campanas;
      this.config = config;
    } catch (e: any) {
      this.error =
        e?.error?.message || 'No pudimos cargar el panel de bonos. Recargá la página.';
    } finally {
      this.cargando = false;
    }
  }

  // ------------------------------------------------------------- los avisos

  /**
   * El módulo apagado es el aviso más importante de la pantalla: se pueden
   * armar campañas y activarlas, y **igual nadie va a cobrar**. Sin decirlo,
   * el operador concluye que el bono no funciona.
   */
  get moduloApagado(): boolean {
    return this.config ? !this.config.activo : false;
  }

  /** Monedas con techo del mes en 0, o sea que no pagan nada. */
  get monedasFrenadas(): string[] {
    const topes = this.config?.topeMensual ?? {};
    return Object.keys(topes)
      .filter((m) => !topes[m])
      .sort();
  }

  /** Monedas cuyo umbral está en 0: toda campaña de esa moneda necesita dos firmas. */
  get monedasConDobleControlTotal(): string[] {
    const u = this.config?.dobleControl ?? {};
    return Object.keys(u)
      .filter((m) => !u[m])
      .sort();
  }

  // ------------------------------------------------------------ la lista

  get visibles(): Campana[] {
    if (!this.filtroEstado) return this.campanas;
    return this.campanas.filter((c) => c.estado === this.filtroEstado);
  }

  contar(estado: string): number {
    return this.campanas.filter((c) => c.estado === estado).length;
  }

  /** Cuánto de la bolsa ya se comprometió, en porcentaje del cupo. */
  avance(c: Campana): number {
    if (!c.unidadesTotal) return 0;
    return Math.min(100, Math.round((c.unidadesUsadas / c.unidadesTotal) * 100));
  }

  /**
   * Una campaña que ya terminó su vigencia pero sigue ACTIVA.
   *
   * No es un error: el motor rechaza los reclamos por fecha igual. Pero verla
   * como «activa» meses después hace que el listado mienta, así que se marca.
   */
  vencida(c: Campana): boolean {
    return c.estado === 'ACTIVA' && new Date(c.vigenciaHasta) < new Date();
  }

  /** Todavía no arrancó: se ve en el listado antes de que alguien pregunte. */
  porEmpezar(c: Campana): boolean {
    return (
      (c.estado === 'ACTIVA' || c.estado === 'PROGRAMADA') &&
      new Date(c.vigenciaDesde) > new Date()
    );
  }

  /**
   * `true` si esta campaña necesita dos firmas.
   *
   * Se calcula con el mismo umbral que usa el backend, y sirve sólo para
   * avisar antes del clic. **La decisión sigue siendo del backend**: acá no se
   * sabe qué operador está mirando la pantalla, así que este cartel dice «hace
   * falta una segunda firma», nunca «te toca a vos».
   */
  necesitaSegundaFirma(c: Campana): boolean {
    if (!this.config) return false;
    // El umbral de LA MONEDA de la campaña: comparar una bolsa en dólares
    // contra un umbral en pesos no quiere decir nada.
    const umbral = this.config.dobleControl?.[c.moneda] ?? 0;
    return Number(c.presupuesto ?? 0) >= umbral;
  }

  /** La moneda de esta campaña todavía no tiene camino para pagar. */
  sinCamino(c: Campana): boolean {
    // CLP hoy: la campaña se crea y se programa, pero sus reclamos se rechazan
    // hasta que exista el asiento. Se avisa en la fila, no sólo al crearla.
    return c.moneda === 'CLP';
  }

  puedeActivar(c: Campana): boolean {
    return c.estado === 'BORRADOR';
  }

  puedePausar(c: Campana): boolean {
    return c.estado === 'ACTIVA' || c.estado === 'PROGRAMADA';
  }

  puedeReanudar(c: Campana): boolean {
    return c.estado === 'PAUSADA';
  }

  puedeCerrar(c: Campana): boolean {
    return c.estado !== 'CERRADA';
  }

  // ------------------------------------------------------------- acciones

  /**
   * Activar. Es el clic que habilita plata, así que se confirma con la bolsa
   * a la vista y con el número que se está autorizando escrito completo.
   */
  async activar(c: Campana): Promise<void> {
    const ok = await Swal.fire({
      title: '¿Activar esta campaña?',
      html:
        `Desde ese momento <b>${c.codigo}</b> acepta reclamos.<br><br>` +
        `Bolsa autorizada: <b>${c.presupuesto} ${c.moneda}</b> ` +
        `(${c.unidadesTotal} bonos de ${c.monto}).` +
        (this.necesitaSegundaFirma(c)
          ? '<br><br>Esta bolsa pasa el umbral de doble control: si vos creaste ' +
            'la campaña, el sistema te lo va a impedir y la tiene que activar ' +
            'otro operador.'
          : ''),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Activar',
      cancelButtonText: 'Volver',
      confirmButtonColor: '#2F4A2B',
    });
    if (!ok.isConfirmed) return;
    await this.mover(c, () => this.bonos.activar(c.bonoCampanaId), 'Campaña activada');
  }

  /** Pausar no pide confirmación de doble firma: frenar nunca es el riesgo. */
  async pausar(c: Campana): Promise<void> {
    const ok = await Swal.fire({
      title: '¿Pausar la campaña?',
      html:
        'No se aceptan reclamos nuevos. Los bonos ya reservados <b>siguen su ' +
        'curso</b> y se acreditan si cumplen su condición.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Pausar',
      cancelButtonText: 'Volver',
      confirmButtonColor: '#2F4A2B',
    });
    if (!ok.isConfirmed) return;
    await this.mover(c, () => this.bonos.pausar(c.bonoCampanaId), 'Campaña pausada');
  }

  /**
   * Reanudar. Si la pausó **la velocidad**, el texto lo dice: el sistema la
   * frenó porque algo se estaba yendo de las manos, y reanudar sin mirar
   * convierte el freno en un cartel.
   */
  async reanudar(c: Campana): Promise<void> {
    const porVelocidad = c.pausadaMotivo === 'VELOCIDAD';
    const ok = await Swal.fire({
      title: porVelocidad ? '¿Reanudar la campaña que se frenó sola?' : '¿Reanudar la campaña?',
      html: porVelocidad
        ? 'La pausó el <b>freno de ritmo</b>: se reclamaron muchos bonos en poco ' +
          'tiempo. Antes de reanudar, mirá el tablero de reclamos — si es un ' +
          'ataque, se ve en los rechazos.<br><br>' +
          'Reanudar exige un <b>segundo operador</b>, igual que activar.'
        : 'Vuelve a aceptar reclamos.',
      icon: porVelocidad ? 'warning' : 'question',
      showCancelButton: true,
      confirmButtonText: 'Reanudar',
      cancelButtonText: 'Volver',
      confirmButtonColor: '#2F4A2B',
    });
    if (!ok.isConfirmed) return;
    await this.mover(c, () => this.bonos.reanudar(c.bonoCampanaId), 'Campaña reanudada');
  }

  /** Cerrar es de una sola dirección: no se reabre, se crea otra. */
  async cerrar(c: Campana): Promise<void> {
    const ok = await Swal.fire({
      title: '¿Cerrar la campaña?',
      html:
        `<b>${c.codigo}</b> no vuelve a abrirse: para eso se crea otra, con otro ` +
        'código.<br><br>Los bonos ya reservados se acreditan si cumplen su ' +
        'condición — romperle la promesa a quien ya la aceptó cuesta más que el bono.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Cerrar la campaña',
      cancelButtonText: 'Volver',
      confirmButtonColor: '#A3432B',
    });
    if (!ok.isConfirmed) return;
    await this.mover(c, () => this.bonos.cerrar(c.bonoCampanaId), 'Campaña cerrada');
  }

  /**
   * Corre una acción y actualiza la fila con lo que devuelve el backend.
   *
   * Se reemplaza la fila con la respuesta en vez de recargar todo: la lista
   * puede tener doscientas campañas y el operador está mirando una.
   *
   * El 403 se trata aparte y **no como un error**: es el doble control
   * diciendo que le toca a otro. El mensaje del backend ya explica por qué —
   * incluido el caso de la misma persona con dos cuentas de trabajo— así que
   * se muestra tal cual en vez de reescribirlo peor.
   */
  private async mover(
    c: Campana,
    accion: () => Promise<Campana>,
    exito: string
  ): Promise<void> {
    if (this.moviendo) return;
    this.moviendo = c.bonoCampanaId;
    try {
      const actualizada = await accion();
      this.reemplazar(actualizada);
      Swal.fire({ title: exito, icon: 'success', confirmButtonColor: '#2F4A2B' });
    } catch (e: any) {
      const msg = e?.error?.message;
      const texto = Array.isArray(msg) ? msg.join(' ') : msg;

      if (e?.status === 403) {
        Swal.fire({
          title: 'Falta la firma de otro operador',
          text: texto || 'Esta campaña necesita un segundo operador.',
          icon: 'info',
          confirmButtonColor: '#2F4A2B',
        });
        return;
      }

      Swal.fire({
        title: 'No se pudo',
        text: texto || 'Intentá de nuevo.',
        icon: 'error',
      });
    } finally {
      this.moviendo = null;
    }
  }

  private reemplazar(actualizada: Campana): void {
    const i = this.campanas.findIndex(
      (x) => x.bonoCampanaId === actualizada.bonoCampanaId
    );
    if (i >= 0) this.campanas[i] = actualizada;
    if (this.campanaAbierta?.bonoCampanaId === actualizada.bonoCampanaId) {
      this.campanaAbierta = actualizada;
    }
  }

  // -------------------------------------------------------------- el detalle

  abrir(c: Campana): void {
    this.campanaAbierta = c;
  }

  volverALista(): void {
    this.campanaAbierta = null;
  }

  // ---------------------------------------------------------------- crear

  abrirNueva(): void {
    this.mostrarNueva = true;
  }

  /** La campaña recién creada se pone arriba: es la que el operador va a mirar. */
  async creada(c: Campana): Promise<void> {
    this.mostrarNueva = false;
    this.campanas = [c, ...this.campanas];
    await Swal.fire({
      title: 'Campaña creada en borrador',
      html:
        `<b>${c.codigo}</b> todavía <b>no acepta reclamos</b>. Revisá los números ` +
        'y activala cuando esté lista.',
      icon: 'success',
      confirmButtonColor: '#2F4A2B',
    });
  }

  cerrarNueva(): void {
    this.mostrarNueva = false;
  }

  // -------------------------------------------------------------- reportes

  async verGasto(): Promise<void> {
    this.vista = 'gasto';
    if (this.gasto.length) return;
    this.cargandoReporte = true;
    try {
      this.gasto = await this.bonos.gasto();
    } catch {
      this.gasto = [];
    } finally {
      this.cargandoReporte = false;
    }
  }

  async verKpi(): Promise<void> {
    this.vista = 'kpi';
    if (this.kpis.length) return;
    this.cargandoReporte = true;
    try {
      this.kpis = await this.bonos.kpi();
    } catch {
      this.kpis = [];
    } finally {
      this.cargandoReporte = false;
    }
  }

  verCampanas(): void {
    this.vista = 'campanas';
  }

  /** Se refresca a mano: el gasto se mira al cerrar el mes, no cada minuto. */
  async refrescarReportes(): Promise<void> {
    this.gasto = [];
    this.kpis = [];
    if (this.vista === 'gasto') await this.verGasto();
    if (this.vista === 'kpi') await this.verKpi();
  }

  // --------------------------------------------------- configuración

  /**
   * Abre el formulario con la configuración fresca del backend.
   *
   * Se vuelve a pedir en vez de usar la que cargó la pantalla: entre que se
   * abrió el panel y este clic pudo pasar media hora, y editar sobre un valor
   * viejo significa reescribir con él un techo que otro operador ya cambió.
   */
  async abrirConfig(): Promise<void> {
    try {
      const c = await this.bonos.config();
      this.original = c;
      // Copia profunda del mapa de techos: sin esto, editar un techo tocaría
      // también el original y `soloCambios()` no vería ninguna diferencia.
      this.editable = {
        ...c,
        topeMensual: { ...c.topeMensual },
        dobleControl: { ...(c.dobleControl ?? {}) },
      };
      this.monedasConTecho = Object.keys(c.topeMensual).sort();
      this.monedasConUmbral = Object.keys(c.dobleControl ?? {}).sort();
      this.mostrarConfig = true;
    } catch {
      Swal.fire({ text: 'No pudimos leer la configuración.', icon: 'error' });
    }
  }

  cerrarConfig(): void {
    this.mostrarConfig = false;
    this.editable = null;
    this.original = null;
  }

  /**
   * Sólo lo que el operador tocó: el backend escribe únicamente eso.
   *
   * `dobleControlClp` no se compara ni se manda: no es editable acá, y el
   * servicio lo saca del cuerpo igual. Ver `BonosService.guardarConfig`.
   */
  private soloCambios(): Partial<ConfigBonos> {
    if (!this.editable || !this.original) return {};
    const cambios: Record<string, unknown> = {};

    for (const k of Object.keys(this.editable) as Array<keyof ConfigBonos>) {
      if (k === 'topeMensual' || k === 'dobleControl') continue;
      if (this.editable[k] !== this.original[k]) cambios[k] = this.editable[k];
    }

    // Los techos van como un mapa con sólo las monedas que cambiaron.
    const techos: Record<string, number> = {};
    for (const m of this.monedasConTecho) {
      const antes = this.original.topeMensual[m];
      const ahora = Number(this.editable.topeMensual[m]);
      if (Number.isFinite(ahora) && ahora !== antes) techos[m] = ahora;
    }
    if (Object.keys(techos).length) cambios['topeMensual'] = techos;

    // Los umbrales, igual: sólo las monedas que se tocaron.
    const umbrales: Record<string, number> = {};
    for (const m of this.monedasConUmbral) {
      const antes = this.original.dobleControl?.[m];
      const ahora = Number(this.editable.dobleControl?.[m]);
      if (Number.isFinite(ahora) && ahora !== antes) umbrales[m] = ahora;
    }
    if (Object.keys(umbrales).length) cambios['dobleControl'] = umbrales;

    return cambios as Partial<ConfigBonos>;
  }

  get hayCambiosConfig(): boolean {
    return Object.keys(this.soloCambios()).length > 0;
  }

  /** `true` si este guardado enciende el módulo. */
  get vaAEncender(): boolean {
    return Boolean(this.editable?.activo && !this.original?.activo);
  }

  /** `true` si este guardado lo apaga. Apagar nunca se confirma: es el freno. */
  get vaAApagar(): boolean {
    return Boolean(!this.editable?.activo && this.original?.activo);
  }

  /**
   * Monedas cuyo umbral de doble control SUBE.
   *
   * Subirlo debilita el control: desde ese monto las campañas dejan de
   * necesitar dos firmas. Es el cambio que a nadie se le ocurre mirar, así que
   * se confirma aparte y el backend lo marca distinto en el canal.
   */
  get umbralesQueAflojan(): string[] {
    if (!this.editable || !this.original) return [];
    return this.monedasConUmbral.filter(
      (m) => Number(this.editable!.dobleControl?.[m]) > Number(this.original!.dobleControl?.[m] ?? 0)
    );
  }

  /** Monedas cuyo techo pasa de 0 a algo: eso habilita pagos que hoy no salen. */
  get techosQueHabilitan(): string[] {
    if (!this.editable || !this.original) return [];
    return this.monedasConTecho.filter(
      (m) => !this.original!.topeMensual[m] && Number(this.editable!.topeMensual[m]) > 0
    );
  }

  /**
   * Guarda. El encendido se confirma aparte, y apagar no.
   *
   * La asimetría es deliberada: encender y subir un techo son las dos acciones
   * que hacen que empiece a salir plata, y ésas piden un clic más. Apagar es el
   * freno, y un freno con fricción es un freno que alguien no va a usar cuando
   * tenga que usarlo.
   */
  async guardarConfig(): Promise<void> {
    const cambios = this.soloCambios();
    if (!Object.keys(cambios).length || this.guardandoConfig) return;

    if (this.vaAEncender || this.techosQueHabilitan.length || this.umbralesQueAflojan.length) {
      const partes: string[] = [];
      if (this.vaAEncender) {
        partes.push(
          'Desde este momento los bonos <b>se empiezan a pagar</b>: las campañas ' +
            'activas van a aceptar reclamos y los jobs van a acreditar.'
        );
      }
      if (this.techosQueHabilitan.length) {
        partes.push(
          `Estás abriendo el gasto del mes en <b>${this.techosQueHabilitan.join(
            ', '
          )}</b>, que hoy está en 0 y no paga nada.`
        );
      }
      if (this.umbralesQueAflojan.length) {
        partes.push(
          `Estás <b>subiendo el umbral de doble control</b> en ` +
            `<b>${this.umbralesQueAflojan.join(', ')}</b>: desde ese monto las ` +
            'campañas van a poder activarse con una sola firma. Queda registrado ' +
            'con tu correo y avisa al canal.'
        );
      }

      const ok = await Swal.fire({
        title: this.vaAEncender ? '¿Encender el módulo de bonos?' : '¿Abrir el gasto del mes?',
        html: partes.join('<br><br>'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.vaAEncender ? 'Encender' : 'Guardar',
        cancelButtonText: 'Volver',
        confirmButtonColor: '#2F4A2B',
      });
      if (!ok.isConfirmed) return;
    }

    this.guardandoConfig = true;
    try {
      const r = await this.bonos.guardarConfig(cambios);
      this.cerrarConfig();
      // Se recarga el panel: los avisos de arriba dependen de esta config.
      await this.cargar();

      const detalle = r.cambios.map((c) => `${c.clave}: ${c.antes} → ${c.ahora}`).join('<br>');
      Swal.fire({
        title: 'Configuración guardada',
        html: detalle || 'Sin cambios.',
        icon: 'success',
        confirmButtonColor: '#2F4A2B',
      });
    } catch (e: any) {
      // El backend valida rangos: un techo negativo o una ventana de 0 vuelven acá.
      const msg = e?.error?.message;
      Swal.fire({
        title: 'No se pudo guardar',
        text: Array.isArray(msg) ? msg.join(' ') : msg || 'Revisá los valores.',
        icon: 'error',
      });
    } finally {
      this.guardandoConfig = false;
    }
  }

  /** El monto como lo lee una persona: `20.000` y no `20000.00000000`. */
  legible(monto: string | number | null | undefined): string {
    return this.bonos.montoLegible(monto);
  }

  /** Cómo se lee el público en la pantalla. El enum no es para mostrar. */
  publicoTexto(p: string): string {
    if (p === 'SIN_ENVIOS') return 'nuevos';
    if (p === 'INACTIVO') return 'inactivos';
    return 'todos';
  }

  condicionTexto(c: string): string {
    if (c === 'AL_FONDEAR') return 'al cargar saldo';
    if (c === 'AL_PRIMER_ENVIO') return 'al primer envío';
    return 'al reclamar';
  }
}
